import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import { Button, SkeletonText, cn } from '@taki/ui';
import type { Inspection, Measurement } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import { ChampTexte, raisonDe, sac } from './morceaux.js';
import {
  MODELE_PAR_DEFAUT,
  changerPoint,
  lireNombre,
  modeParDefaut,
  pointsDeLExamen,
  resumeDeLExamen,
  toutNormal,
  verdictDe,
} from './examen.js';
import type { EtatDuPoint, PointExamine } from './examen.js';
import { nombreEnTexte } from './nombre.js';

/**
 * L'ONGLET « EXAMEN » DU DOSSIER : l'examen clinique en liste (logique et
 * decisions dans `examen.ts`). Chaque geste s'enregistre aussitot :
 *   - un etat, un choix rapide, « tout normal » : les `checkpoints` de
 *     l'inspection `examen` de la consultation, creee au premier geste ;
 *   - une note de ligne : a Entree ou en quittant le champ ;
 *   - une constante : une `measurement` par grandeur, liee a l'animal et a la
 *     consultation (`careRecordId`), archivee quand on vide le champ.
 * Le texte libre de l'examen entier reste le champ `exam` de la consultation :
 * la porte declaree « Consultations » et l'historique le lisent deja.
 */

const CLASSES_DU_CHAMP =
  'w-full rounded-sm border border-border bg-surface-1 px-3 text-sm text-ink-900 placeholder:text-ink-300 ' +
  'focus-visible:outline-none focus-visible:border-accent-border';

export interface ProprietesDeLExamen {
  sdk: TakiSdk;
  orgId: string;
  consultationId: string;
  assetId: string;
  espece: unknown;
  moi: string;
  /** Le texte libre de l'examen entier : le champ `exam` de la consultation. */
  texteLibre: unknown;
  ecrireTexteLibre: (valeur: string | null) => void;
  /** Le titre de la section dans le compte rendu, et son ancre pour le sommaire. */
  titre: string;
  ancre: string;
  /** Le nombre de points examines, remonte au dossier (pastille de l'onglet, cloture). */
  onExamines?: (nombre: number) => void;
}

export function Examen(props: ProprietesDeLExamen): JSX.Element {
  const { sdk, orgId, consultationId, assetId, espece, moi } = props;
  const modele = MODELE_PAR_DEFAUT;
  const [examen, setExamen] = useState<Inspection | null>(null);
  const [points, setPoints] = useState<PointExamine[]>(() => pointsDeLExamen(modele, []));
  const [mesures, setMesures] = useState<readonly Measurement[]>([]);
  const [mode, setMode] = useState<'liste' | 'texte' | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  /* Un examen deja complet s'ouvre replie sur son resume. */
  const [replie, setReplie] = useState(false);

  /* La lecture : l'examen de cette consultation, ses constantes, et les derniers examens de ce veterinaire (son mode). */
  useEffect(() => {
    let vivant = true;
    void Promise.all([
      sdk.inspections.list({ type: 'examen', where: { 'customFields.consultationId': consultationId }, limit: 5 }),
      sdk.measurements.list({ type: 'constante', where: { careRecordId: consultationId }, limit: 50 }),
      moi === '' ? Promise.resolve([] as Inspection[]) : sdk.inspections.list({ type: 'examen', where: { 'customFields.examinePar': moi }, limit: 200 }),
    ]).then(
      ([desExamens, desMesures, mesExamens]) => {
        if (!vivant) return;
        const trouve = desExamens[0] ?? null;
        setExamen(trouve);
        const lus = pointsDeLExamen(modele, trouve?.checkpoints ?? []);
        setPoints(lus);
        setReplie(lus.every((p) => p.etat !== 'nonExamine'));
        setMesures(desMesures);
        const stocke = sac(trouve)['mode'];
        setMode(
          stocke === 'liste' || stocke === 'texte'
            ? stocke
            : modeParDefaut(mesExamens.map((e) => ({ mode: sac(e)['mode'], quand: e.createdAt.getTime() })), espece),
        );
      },
      (cause: unknown) => vivant && setErreur(`Examen illisible : ${raisonDe(cause)}`),
    );
    return () => {
      vivant = false;
    };
  }, [sdk, consultationId, moi, espece, modele]);

  /*
   * UN SEUL EXAMEN PAR CONSULTATION : la creation est une promesse partagee.
   * Deux gestes rapides avant le retour du serveur attendent la meme fiche au
   * lieu d'en creer deux.
   */
  const enCreation = useRef<Promise<Inspection> | null>(null);
  const examenOuCreation = useCallback(async (): Promise<Inspection> => {
    if (examen !== null) return examen;
    if (enCreation.current === null) {
      enCreation.current = sdk.inspections.create({
        orgId,
        type: 'examen',
        ...(assetId === '' ? {} : { assetId }),
        performedAt: new Date(),
        checkpoints: [],
        customFields: { consultationId, ...(moi === '' ? {} : { examinePar: moi }), mode: mode ?? 'liste' },
      });
      enCreation.current.then(setExamen, () => (enCreation.current = null));
    }
    return enCreation.current;
  }, [examen, sdk, orgId, assetId, consultationId, moi, mode]);

  /* Chaque geste repart des points AFFICHES : l'ecran est la verite, le serveur la recoit. */
  const enregistrer = async (suivants: PointExamine[]): Promise<void> => {
    setPoints(suivants);
    try {
      const fiche = await examenOuCreation();
      const verdict = verdictDe(suivants);
      await sdk.inspections.update(fiche.id, {
        checkpoints: suivants.map((p) => ({ ...p })),
        ...(verdict === null ? {} : { verdict }),
        performedAt: new Date(),
      });
      setErreur(null);
    } catch (cause) {
      setErreur(`Examen non enregistré : ${raisonDe(cause)}`);
    }
  };
  const changerMode = async (suivant: 'liste' | 'texte'): Promise<void> => {
    setMode(suivant);
    try {
      const fiche = await examenOuCreation();
      await sdk.inspections.update(fiche.id, { customFields: { mode: suivant } });
    } catch (cause) {
      setErreur(`Mode non enregistré : ${raisonDe(cause)}`);
    }
  };

  /* Les constantes : la derniere valeur active par grandeur. */
  const valeurs = useMemo(() => {
    const parGrandeur: Record<string, number> = {};
    for (const m of mesures) if (m.archivedAt == null) parGrandeur[m.metric] = m.value;
    return parGrandeur;
  }, [mesures]);
  const ecrireMesure = async (metric: string, unite: string, saisie: string): Promise<void> => {
    const nombre = lireNombre(saisie);
    const existante = mesures.find((m) => m.metric === metric && m.archivedAt == null);
    if (nombre === (existante?.value ?? null)) return;
    if (saisie.trim() !== '' && nombre === null) {
      setErreur(`« ${saisie} » n'est pas un nombre.`);
      return;
    }
    try {
      if (existante !== undefined && nombre === null) {
        await sdk.measurements.archive(existante.id);
        setMesures((avant) => avant.filter((m) => m.id !== existante.id));
      } else if (existante !== undefined && nombre !== null) {
        const nouvelle = await sdk.measurements.update(existante.id, { value: nombre, measuredAt: new Date() });
        setMesures((avant) => avant.map((m) => (m.id === nouvelle.id ? nouvelle : m)));
      } else if (nombre !== null) {
        const fiche = await examenOuCreation();
        const nouvelle = await sdk.measurements.create({
          orgId,
          type: 'constante',
          ...(assetId === '' ? {} : { assetId }),
          careRecordId: consultationId,
          inspectionId: fiche.id,
          metric,
          value: nombre,
          unit: unite,
          measuredAt: new Date(),
        });
        setMesures((avant) => [...avant, nouvelle]);
      }
      setErreur(null);
    } catch (cause) {
      setErreur(`Constante non enregistrée : ${raisonDe(cause)}`);
    }
  };

  const resume = resumeDeLExamen(modele, points, valeurs);
  const examines = points.filter((p) => p.etat !== 'nonExamine').length;
  const { onExamines } = props;
  useEffect(() => onExamines?.(examines), [onExamines, examines]);

  if (mode === null) {
    return (
      <section id={props.ancre} className="flex scroll-mt-20 flex-col gap-5">
        <div className="flex flex-wrap items-center gap-3 border-b border-border-soft pb-2">
          <h3 className="text-base font-semibold text-ink-900">{props.titre}</h3>
        </div>
        {erreur === null ? <SkeletonText lines={4} /> : <p className="text-sm text-danger">{erreur}</p>}
      </section>
    );
  }

  const toutesLesMesures = modele.flatMap((s) => s.mesures);
  const remarque = typeof props.texteLibre === 'string' ? props.texteLibre.trim() : '';

  return (
    <section id={props.ancre} className="flex scroll-mt-20 flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3 border-b border-border-soft pb-2">
        <h3 className="text-base font-semibold text-ink-900">{props.titre}</h3>
        <div className="flex gap-1">
          <Button size="sm" variant={mode === 'liste' ? 'dark' : 'secondary'} onClick={() => void changerMode('liste')}>
            Liste
          </Button>
          <Button size="sm" variant={mode === 'texte' ? 'dark' : 'secondary'} onClick={() => void changerMode('texte')}>
            Texte libre
          </Button>
        </div>
        {mode === 'liste' ? (
          <span className="text-sm text-ink-500">
            {examines} / {points.length} examinés
          </span>
        ) : null}
        <span className="flex-1" />
        <Button size="sm" variant="ghost" onClick={() => setReplie(!replie)}>
          {replie ? 'Déplier' : 'Replier'}
        </Button>
      </div>
      {erreur !== null ? <p className="text-sm text-danger">{erreur}</p> : null}

      {replie ? (
        /* REPLIE : l'examen se lit en son resume, la page reste courte. */
        <div className="flex flex-col gap-1 text-sm text-ink-900">
          {resume.length === 0 && remarque === '' ? <p className="text-ink-400">Rien d'examiné.</p> : null}
          {resume.map((ligne) => (
            <p key={ligne}>{ligne}</p>
          ))}
          {remarque === '' ? null : <p className="whitespace-pre-wrap text-ink-700">{remarque}</p>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {toutesLesMesures.map((m) => (
              <Constante
                key={`${consultationId}-${m.metric}`}
                libelle={`${m.libelle} (${m.unite})`}
                valeur={valeurs[m.metric]}
                onEcrire={(saisie) => void ecrireMesure(m.metric, m.unite, saisie)}
              />
            ))}
          </div>

          {mode === 'liste' ? (
            <div className="flex flex-col gap-5">
              {modele.map((systeme) => {
                const siens = points.filter((p) => p.systeme === systeme.cle);
                return (
                  <div key={systeme.cle} className="flex flex-col">
                    <div className="flex items-center gap-3 border-b border-border-soft pb-1">
                      <h4 className="flex-1 text-sm font-semibold text-ink-700">{systeme.libelle}</h4>
                      {siens.some((p) => p.etat === 'nonExamine') ? (
                        <Button size="sm" variant="ghost" onClick={() => void enregistrer(toutNormal(points, systeme.cle))}>
                          Tout normal
                        </Button>
                      ) : null}
                    </div>
                    {siens.map((point) => (
                      <LigneDuPoint
                        key={`${consultationId}-${point.cle}`}
                        point={point}
                        choixPossibles={systeme.points.find((d) => d.cle === point.cle)?.choix ?? []}
                        afficherLibelle={siens.length > 1 || point.libelle !== systeme.libelle}
                        onChanger={(patch) => void enregistrer(changerPoint(points, point.cle, patch))}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ) : null}

          <ChampTexte
            key={`${consultationId}-exam`}
            libelle={mode === 'liste' ? 'Remarque sur l\'examen' : 'Yeux, oreilles, muqueuses, auscultation'}
            valeur={props.texteLibre}
            onEcrire={props.ecrireTexteLibre}
          />
        </>
      )}
    </section>
  );
}

/** Une ligne de l'examen : le libelle, Normal / Anormal, la note ; anormal ouvre les choix rapides. */
function LigneDuPoint({
  point,
  choixPossibles,
  afficherLibelle,
  onChanger,
}: {
  point: PointExamine;
  choixPossibles: readonly string[];
  afficherLibelle: boolean;
  onChanger: (patch: Partial<Pick<PointExamine, 'etat' | 'choix' | 'note'>>) => void;
}): JSX.Element {
  const [note, setNote] = useState(point.note);
  useEffect(() => setNote(point.note), [point.note]);
  const ecrireNote = (): void => {
    if (note.trim() !== point.note.trim()) onChanger({ note: note.trim() });
  };
  /* Un second clic sur l'etat choisi le defait aussi : retour a « non examine ». */
  const basculer = (etat: EtatDuPoint): void => onChanger({ etat: point.etat === etat ? 'nonExamine' : etat });
  const choixAffiches = [...choixPossibles, ...point.choix.filter((c) => !choixPossibles.includes(c))];
  return (
    <div className={cn('flex flex-col gap-2 border-b border-border-soft py-2', point.etat === 'anormal' && '-mx-2 bg-danger-tint px-2')}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn('w-40 shrink-0 text-sm', point.etat === 'nonExamine' ? 'text-ink-500' : 'text-ink-900')}>
          {afficherLibelle ? point.libelle : ''}
        </span>
        <Button size="sm" variant={point.etat === 'normal' ? 'dark' : 'secondary'} onClick={() => basculer('normal')}>
          Normal
        </Button>
        <button
          type="button"
          onClick={() => basculer('anormal')}
          className={cn(
            'h-8 rounded-sm border px-3 text-sm',
            point.etat === 'anormal'
              ? 'border-danger-border bg-danger text-white'
              : 'border-border bg-surface text-ink-900 hover:border-danger-border',
          )}
        >
          Anormal
        </button>
        <button
          type="button"
          onClick={() => onChanger({ etat: 'nonExamine' })}
          aria-pressed={point.etat === 'nonExamine'}
          className={cn(
            'h-8 rounded-sm border px-3 text-sm',
            point.etat === 'nonExamine'
              ? 'border-border-strong bg-surface-3 text-ink-700'
              : 'border-border bg-surface text-ink-500 hover:border-border-strong',
          )}
        >
          Non examiné
        </button>
        <input
          className={cn(CLASSES_DU_CHAMP, 'h-8 min-w-40 flex-1')}
          value={note}
          placeholder="Note"
          onChange={(e) => setNote(e.target.value)}
          onBlur={ecrireNote}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') ecrireNote();
          }}
        />
      </div>
      {point.etat === 'anormal' && choixAffiches.length > 0 ? (
        <div className="flex flex-wrap gap-2 sm:pl-[10.5rem]">
          {choixAffiches.map((c) => {
            const coche = point.choix.includes(c);
            return (
              <button
                key={c}
                type="button"
                onClick={() => onChanger({ choix: coche ? point.choix.filter((x) => x !== c) : [...point.choix, c] })}
                className={cn(
                  'h-7 rounded-sm border px-2 text-xs',
                  coche ? 'border-danger-border bg-danger text-white' : 'border-danger-border bg-surface-1 text-danger-ink',
                )}
              >
                {c}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** Une constante : un nombre, enregistre a Entree ou en quittant le champ. */
function Constante({ libelle, valeur, onEcrire }: { libelle: string; valeur: number | undefined; onEcrire: (saisie: string) => void }): JSX.Element {
  const initial = valeur === undefined ? '' : nombreEnTexte(valeur);
  const [saisie, setSaisie] = useState(initial);
  useEffect(() => setSaisie(initial), [initial]);
  const ecrire = (): void => {
    if (saisie.trim() !== initial) onEcrire(saisie);
  };
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-ink-500">{libelle}</span>
      <input
        className={cn(CLASSES_DU_CHAMP, 'h-9 tabular-nums')}
        inputMode="decimal"
        value={saisie}
        onChange={(e) => setSaisie(e.target.value)}
        onBlur={ecrire}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') ecrire();
        }}
      />
    </label>
  );
}
